<template>
  <div class="page-container">
    <!-- 功能关闭提示 -->
    <el-alert
      v-if="!saleConfig.balanceSaleEnable && !saleConfig.pointsSaleEnable"
      title="余额售卖和积分售卖均已关闭，如需开启请修改服务端 .env 中的 BALANCE_SALE_ENABLE / POINTS_SALE_ENABLE"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom:12px"
    />
    <el-alert
      v-else-if="!saleConfig.balanceSaleEnable"
      title="余额售卖已关闭（BALANCE_SALE_ENABLE=false），套餐仍可配置，但用户端无法购买"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom:12px"
    />
    <el-alert
      v-else-if="!saleConfig.pointsSaleEnable"
      title="积分售卖已关闭（POINTS_SALE_ENABLE=false），套餐仍可配置，但用户端无法购买"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom:12px"
    />

    <el-card shadow="never">
      <div slot="header" class="card-header">
        <el-tabs v-model="activeTab" class="header-tabs">
          <el-tab-pane label="余额套餐" name="balance">
            <span slot="label">
              <i class="el-icon-money"></i> 余额套餐
              <el-tag v-if="!saleConfig.balanceSaleEnable" type="danger" size="mini" style="margin-left:4px">已关闭</el-tag>
            </span>
          </el-tab-pane>
          <el-tab-pane label="积分套餐" name="points">
            <span slot="label">
              <i class="el-icon-medal"></i> 积分套餐
              <el-tag v-if="!saleConfig.pointsSaleEnable" type="danger" size="mini" style="margin-left:4px">已关闭</el-tag>
            </span>
          </el-tab-pane>
        </el-tabs>
        <el-button
          v-permission="['sale:package:create']"
          type="success"
          size="mini"
          icon="el-icon-plus"
          @click="handleCreate"
        >新增套餐</el-button>
      </div>

      <!-- 余额套餐 -->
      <el-table
        v-show="activeTab === 'balance'"
        :data="balancePackages"
        v-loading="loading"
        border
        stripe
        size="small"
      >
        <el-table-column type="index" label="#" width="55" align="center" />
        <el-table-column prop="title" label="套餐标题" min-width="140" />
        <el-table-column label="售价（元）" width="110" align="right">
          <template slot-scope="{ row }">
            <span class="price-text">¥ {{ row.price.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="到账余额（元）" width="130" align="right">
          <template slot-scope="{ row }">
            <span class="amount-text">{{ row.amount.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="70" align="center" />
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="165" />
        <el-table-column label="操作" width="130" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['sale:package:update']"
              type="primary"
              size="mini"
              @click="handleEdit(row)"
            >编辑</el-button>
            <el-button
              v-permission="['sale:package:delete']"
              type="danger"
              size="mini"
              @click="handleDelete(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 积分套餐 -->
      <el-table
        v-show="activeTab === 'points'"
        :data="pointsPackages"
        v-loading="loading"
        border
        stripe
        size="small"
      >
        <el-table-column type="index" label="#" width="55" align="center" />
        <el-table-column prop="title" label="套餐标题" min-width="140" />
        <el-table-column label="售价（元）" width="110" align="right">
          <template slot-scope="{ row }">
            <span class="price-text">¥ {{ row.price.toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="到账积分（个）" width="130" align="right">
          <template slot-scope="{ row }">
            <span class="amount-text">{{ parseInt(row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="70" align="center" />
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'info'" size="mini">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="165" />
        <el-table-column label="操作" width="130" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-permission="['sale:package:update']"
              type="primary"
              size="mini"
              @click="handleEdit(row)"
            >编辑</el-button>
            <el-button
              v-permission="['sale:package:delete']"
              type="danger"
              size="mini"
              @click="handleDelete(row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="480px"
      @close="resetForm"
    >
      <el-form
        ref="packageForm"
        :model="form"
        :rules="rules"
        label-width="100px"
        size="small"
      >
        <el-form-item label="套餐类型" prop="type">
          <el-radio-group v-model="form.type" :disabled="!!form.id">
            <el-radio label="balance">余额套餐</el-radio>
            <el-radio label="points">积分套餐</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="套餐标题" prop="title">
          <el-input v-model="form.title" placeholder="如：30元充30元" clearable />
        </el-form-item>

        <el-form-item label="售价（元）" prop="price">
          <el-input-number
            v-model="form.price"
            :min="0.01"
            :precision="2"
            :step="1"
            style="width:180px"
          />
        </el-form-item>

        <el-form-item prop="amount">
          <span slot="label">
            {{ form.type === 'points' ? '到账积分（个）' : '到账余额（元）' }}
          </span>
          <el-input-number
            v-model="form.amount"
            :min="0.01"
            :precision="form.type === 'points' ? 0 : 2"
            :step="form.type === 'points' ? 100 : 1"
            style="width:180px"
          />
        </el-form-item>

        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" :min="0" :max="999" style="width:120px" />
          <span class="form-tip">数值越小排越前</span>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button size="mini" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="submitting" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getSaleConfig,
  getSalePackageList,
  createSalePackage,
  updateSalePackage,
  deleteSalePackage
} from '@/api/sale'

const defaultForm = () => ({
  id: null,
  type: 'balance',
  title: '',
  price: 1.00,
  amount: 1.00,
  sort: 0,
  status: 1
})

export default {
  name: 'SalePackage',
  data() {
    return {
      loading: false,
      activeTab: 'balance',
      balancePackages: [],
      pointsPackages: [],
      saleConfig: {
        balanceSaleEnable: true,
        pointsSaleEnable: true
      },
      // 弹窗
      dialogVisible: false,
      submitting: false,
      form: defaultForm(),
      rules: {
        type:   [{ required: true, message: '请选择套餐类型', trigger: 'change' }],
        title:  [{ required: true, message: '请输入套餐标题', trigger: 'blur' }],
        price:  [{ required: true, type: 'number', min: 0.01, message: '售价必须大于 0', trigger: 'change' }],
        amount: [{ required: true, type: 'number', min: 0.01, message: '到账数量必须大于 0', trigger: 'change' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.form.id ? '编辑套餐' : '新增套餐'
    }
  },
  created() {
    this.fetchConfig()
    this.fetchData()
  },
  methods: {
    async fetchConfig() {
      try {
        const res = await getSaleConfig()
        this.saleConfig = res.data || { balanceSaleEnable: true, pointsSaleEnable: true }
      } catch {}
    },

    async fetchData() {
      this.loading = true
      try {
        const res = await getSalePackageList()
        const data = res.data || {}
        this.balancePackages = data.balance || []
        this.pointsPackages  = data.points  || []
      } finally {
        this.loading = false
      }
    },

    handleCreate() {
      this.form = defaultForm()
      this.form.type = this.activeTab
      this.dialogVisible = true
    },

    handleEdit(row) {
      this.form = {
        id:     row.id,
        type:   row.type,
        title:  row.title,
        price:  row.price,
        amount: row.amount,
        sort:   row.sort,
        status: row.status
      }
      this.dialogVisible = true
    },

    async handleDelete(row) {
      const typeLabel = row.type === 'balance' ? '余额' : '积分'
      try {
        await this.$confirm(
          `确定删除${typeLabel}套餐「${row.title}」吗？`,
          '提示',
          { type: 'warning' }
        )
        await deleteSalePackage({ id: row.id })
        this.$message.success('删除成功')
        this.fetchData()
      } catch {}
    },

    async handleSubmit() {
      try {
        await this.$refs.packageForm.validate()
        this.submitting = true
        const data = { ...this.form }
        if (data.id) {
          await updateSalePackage(data)
        } else {
          delete data.id
          await createSalePackage(data)
        }
        this.$message.success(data.id ? '更新成功' : '创建成功')
        this.dialogVisible = false
        this.activeTab = this.form.type
        this.fetchData()
      } catch {} finally {
        this.submitting = false
      }
    },

    resetForm() {
      this.$refs.packageForm && this.$refs.packageForm.resetFields()
    }
  }
}
</script>

<style scoped>
.page-container { padding: 16px; }

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-tabs {
  flex: 1;
  margin-bottom: -18px;
}

.price-text {
  color: #f56c6c;
  font-weight: 600;
}

.amount-text {
  color: #409eff;
  font-weight: 600;
}

.form-tip {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}
</style>
